import {
  blue,
  buyerResearchStageColors,
  gold,
  green,
  magenta,
  neutral,
  seriesPalette,
} from "@intentsify/brand";
import { barY, defineChart, facet, stack } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { BrandChartTheme } from "../BrandChart";
import { formatTickValue } from "../Charts.utils";
import { StackedGroupedBarChartItem } from "./StackedGroupedBarChart.types";

/** One stacked segment: a key's value inside one sub-bar of one group. */
export type StackedGroupedBarRow = {
  group: string;
  bar: string;
  barIndex: number;
  key: string;
  value: number;
  additionalTooltipContent: string | undefined;
};

type StageName = keyof typeof buyerResearchStageColors;

const darkStageColors: Record<StageName, string> = {
  Awareness: magenta[300],
  Interest: blue[300],
  Consideration: gold[300],
  Decision: green[500],
};

/** Totals next to stage stacks: present but quieter than the stages. */
const totalColor = { light: neutral[300], dark: neutral[600] } as const;

const isStage = (key: string): key is StageName =>
  key in buyerResearchStageColors;

/**
 * Brand colours by meaning: buyer research stages keep their stage colours,
 * other segments in a chart that also shows stages (e.g. "All Accounts") are
 * neutral, and anything else is coloured by sub-bar position.
 */
export const createStackedGroupedBarColors = (
  data: StackedGroupedBarChartItem[],
  mode: BrandChartTheme["mode"],
) => {
  const barLabels = [
    ...new Set(data.flatMap((group) => group.groupItems.map((i) => i.label))),
  ];
  const hasStages = data.some((group) =>
    group.groupItems.some((item) => item.data.some((d) => isStage(d.label))),
  );
  const palette = seriesPalette[mode];
  const byPosition = (index: number) => palette[index % palette.length];

  const colorFor = (key: string, barIndex: number) => {
    if (isStage(key)) {
      return mode === "light"
        ? buyerResearchStageColors[key]
        : darkStageColors[key];
    }

    return hasStages ? totalColor[mode] : byPosition(barIndex);
  };

  /** Legend entries may name a stack key or a sub-bar. */
  const legendColorFor = (label: string) => {
    const barIndex = barLabels.indexOf(label);

    return barIndex >= 0 && !isStage(label)
      ? byPosition(barIndex)
      : colorFor(label, 0);
  };

  return { colorFor, legendColorFor };
};

/**
 * Flattens groups into stack segments. Keys are reversed so the first data
 * entry sits on top of the stack, as in the visx chart.
 */
export const toStackedGroupedBarRows = (
  data: StackedGroupedBarChartItem[],
): StackedGroupedBarRow[] =>
  data.flatMap((group) =>
    group.groupItems.flatMap((item, barIndex) =>
      [...item.data].reverse().map((segment) => ({
        group: group.groupLabel,
        bar: item.label,
        barIndex,
        key: segment.label,
        value: Number(segment.value) || 0,
        additionalTooltipContent: segment.metaData?.additionalTooltipContent,
      })),
    ),
  );

export const getMaxStackTotal = (rows: StackedGroupedBarRow[]) => {
  const totals = new Map<string, number>();

  for (const row of rows) {
    const id = `${row.group}\u0000${row.bar}`;
    totals.set(id, (totals.get(id) ?? 0) + row.value);
  }

  return Math.max(0, ...totals.values());
};

export type StackedGroupedBarDefinitionOptions = {
  brandTheme: BrandChartTheme;
  valueAxisLabel?: string;
  maxYValue?: number;
  hideGroupTicks?: boolean;
  hideBarsLabels?: boolean;
};

export const buildStackedGroupedBarDefinition = (
  data: StackedGroupedBarChartItem[],
  {
    brandTheme,
    valueAxisLabel,
    maxYValue,
    hideGroupTicks = false,
    hideBarsLabels = false,
  }: StackedGroupedBarDefinitionOptions,
) => {
  const rows = toStackedGroupedBarRows(data);
  const { colorFor } = createStackedGroupedBarColors(data, brandTheme.mode);
  // One configured instance so every group shares the value axis.
  const y = scaleLinear()
    .domain([0, Math.max(maxYValue ?? 0, getMaxStackTotal(rows), 1)])
    .nice();

  const firstGroup = rows[0]?.group;
  const maxBars = Math.max(1, ...data.map((group) => group.groupItems.length));

  const panels = facet(rows, {
    by: "group",
    columns: Math.max(1, data.length),
    gap: 12,
    // Groups can hold different bars (e.g. one has no "Trending" bar), so
    // each panel keeps its own x scale; the value axis is drawn once.
    axes: "cell",
    label: hideGroupTicks ? false : (group) => String(group),
    chart: (groupRows, { key }) => ({
      marks: [
        barY(groupRows, {
          x: "bar",
          y: "value",
          z: "key",
          key: (row) => `${row.bar}:${row.key}`,
          fill: (row) => colorFor(row.key, row.barIndex),
          layout: stack(),
          radius: { end: 3, stack: "outer" },
        }),
      ],
      scales: {
        x: {
          scale: (() => {
            const bars = [...new Set(groupRows.map((row) => row.bar))];

            // Extra outer padding for groups with fewer bars keeps every bar
            // the same width as in the fullest group.
            return scaleBand<string>()
              .domain(bars)
              .paddingInner(0.2)
              .paddingOuter(0.15 + (maxBars - bars.length) / 2);
          })(),
          axis: hideBarsLabels ? false : { ticks: { size: 0 } },
        },
        y: {
          scale: y,
          grid: true,
          axis:
            key === firstGroup
              ? {
                  line: false,
                  label: valueAxisLabel,
                  ticks: {
                    size: 0,
                    format: (value: number) => formatTickValue(value),
                  },
                }
              : false,
        },
      },
    }),
  });

  // The facet owns each panel's scales; the outer chart only hosts it.
  return defineChart({
    theme: brandTheme.theme,
    marks: [panels],
    scales: { x: null, y: null },
  });
};
